import { AddressAutocompleteParameters } from "stentor-models";

import { err } from ".";

export interface AddressAutocompleteConfig {
    apiKey?: string;
    baseUrl?: string;
}

export interface AddressAutocompleteQuery {
    input: string;
    componentRestrictions?: {
        country: "US";
    };
    params?: AddressAutocompleteParameters;
}

export interface MainTextMatchedSubstrings {
    offset: number;
    length: number;
}

export interface StructuredFormatting {
    main_text: string;
    secondary_text: string;
    main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}

export interface PlaceType {
    description: string;
    structured_formatting: StructuredFormatting;
    /**
     * Google Place ID for the prediction, used to resolve full place details
     * (the prediction description never includes the postal code).
     */
    placeId?: string;
}

/**
 * Place details from the New Places API (v1). Only the fields the widget uses.
 */
export interface PlaceDetails {
    id?: string;
    formattedAddress?: string;
}

// New Places API (v1) response types
interface NewApiMatch {
    startOffset?: number;
    endOffset: number;
}

interface NewApiFormattedText {
    text: string;
    matches?: NewApiMatch[];
}

interface NewApiPlacePrediction {
    placeId: string;
    text: NewApiFormattedText;
    structuredFormat: {
        mainText: NewApiFormattedText;
        secondaryText?: { text: string };
    };
}

// The New Places API can return placePrediction or queryPrediction items
interface NewApiSuggestion {
    placePrediction?: NewApiPlacePrediction;
}

interface PlaceSuggestion {
    placePrediction: NewApiPlacePrediction;
}

interface NewApiResponse {
    suggestions?: NewApiSuggestion[];
}

function isPlaceSuggestion(s: NewApiSuggestion): s is PlaceSuggestion {
    return s.placePrediction != null;
}

function mapSuggestion(suggestion: PlaceSuggestion): PlaceType {
    const { placePrediction } = suggestion;
    return {
        placeId: placePrediction.placeId,
        description: placePrediction.text.text,
        structured_formatting: {
            main_text: placePrediction.structuredFormat.mainText.text,
            secondary_text: placePrediction.structuredFormat.secondaryText?.text || "",
            main_text_matched_substrings: (
                placePrediction.structuredFormat.mainText.matches || []
            ).map((m) => ({
                offset: m.startOffset ?? 0,
                length: (m.endOffset ?? 0) - (m.startOffset ?? 0),
            })),
        },
    };
}

/**
 * A service that interacts with a Google Places Autocomplete compatible API (New Places API v1).
 */
export class AddressAutocompleteService {
    private readonly baseUrl: string;
    private readonly key?: string;

    constructor(config: AddressAutocompleteConfig) {
        this.baseUrl = config.baseUrl || "https://api.xapp.ai";
        this.key = config.apiKey;
    }

    public getPlacePredictions(
        query: AddressAutocompleteQuery,
        callback: (results?: readonly PlaceType[]) => void
    ) {
        const requestBody: Record<string, unknown> = {};

        // Translate legacy params to New Places API v1 fields
        if (query.params) {
            // Convert legacy "components" (e.g. "country:us" or "country:us|country:ca")
            // to New API "includedRegionCodes" (e.g. ["us", "ca"])
            if (query.params.components) {
                const regionCodes = query.params.components
                    .split("|")
                    .filter((c) => c.startsWith("country:"))
                    .map((c) => c.replace("country:", "").toLowerCase());
                if (regionCodes.length > 0) {
                    requestBody.includedRegionCodes = regionCodes;
                }
            }
            // Convert legacy "language" to New API "languageCode"
            if (query.params.language) {
                requestBody.languageCode = query.params.language;
            }
            // Convert legacy "location" (lat,lng string) + "radius" to New API "locationBias"
            if (query.params.location) {
                const [lat, lng] = query.params.location.split(",").map(Number);
                if (!isNaN(lat) && !isNaN(lng)) {
                    requestBody.locationBias = {
                        circle: {
                            center: { latitude: lat, longitude: lng },
                            radius: Number(query.params.radius) || 50000,
                        },
                    };
                }
            }
        }

        requestBody.input = query.input;

        // componentRestrictions overrides params; default to US unless already set
        if (query.componentRestrictions?.country) {
            requestBody.includedRegionCodes = [query.componentRestrictions.country.toLowerCase()];
        } else if (!requestBody.includedRegionCodes) {
            requestBody.includedRegionCodes = ["us"];
        }

        let url = `${this.baseUrl}/places/autocomplete`;
        if (this.key) {
            url += `?key=${encodeURIComponent(this.key)}`;
        }

        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`getPlacePredictions: HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((data: NewApiResponse) => {
                callback((data.suggestions || []).filter(isPlaceSuggestion).map(mapSuggestion));
            })
            .catch((error) => {
                err("getPlacePredictions: Fetch error:", error);
                callback([]);
            });
    }

    /**
     * Resolves a place id from a prediction to full place details, which include
     * the formatted address with postal code. Calls back with undefined on failure.
     */
    public getPlaceDetails(placeId: string, callback: (details?: PlaceDetails) => void) {
        let url = `${this.baseUrl}/places/details/${encodeURIComponent(placeId)}`;
        if (this.key) {
            url += `?key=${encodeURIComponent(this.key)}`;
        }

        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`getPlaceDetails: HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((data: PlaceDetails) => {
                callback(data);
            })
            .catch((error) => {
                err("getPlaceDetails: Fetch error:", error);
                callback(undefined);
            });
    }
}
